import { Injectable, OnDestroy, inject } from '@angular/core';
import { forkJoin, from, Observable, of, Subscription, switchMap } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { BsHubService, AwsApiWrapperService, BsCacheService } from '@brightside-web/desktop/data-access/core-services';
import {
  RequiredVerification,
  RequiredVerificationsEnums,
  VerificationStatus,
  EligibilityFieldsObject,
  EligibilityFieldsInterface,
  ApiCacheService,
  VerificationStatusInterface,
  CompanyService,
  MobileStateResponse,
} from '..';
import { MobileStateService } from './mobile-state.service';

/**
 * When `'1'` in localStorage, eligibility data-collect injects empty `last_four_ssn` (dev menu).
 * Uses localStorage so the toggle survives Auth0 redirect when set before sign-in.
 */
export const DEV_INJECT_ELIGIBILITY_LAST4_KEY = 'brightside_dev_inject_eligibility_last4';

/** @deprecated Use {@link DEV_INJECT_ELIGIBILITY_LAST4_KEY} */
export const DEV_INJECT_ELIGIBILITY_LAST4_SESSION_KEY = DEV_INJECT_ELIGIBILITY_LAST4_KEY;

export function readEligibilityLast4DevInject(): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  return localStorage.getItem(DEV_INJECT_ELIGIBILITY_LAST4_KEY) === '1';
}

export function writeEligibilityLast4DevInject(enabled: boolean): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(DEV_INJECT_ELIGIBILITY_LAST4_KEY, enabled ? '1' : '0');
}

/** BsCache key: feed guard skips eligibility data-collect when set. */
export const HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY = 'hasCheckedEligibilityDataCollect';

/** Maps `/client/mobilestate` `sf_record_type` to eligibility `employment_type` API values. */
export function employmentTypeFromSfRecordType(sfRecordType: string | null | undefined): string {
  if (!sfRecordType || sfRecordType === 'employee') {
    return 'employment';
  }
  return 'dependent';
}

/** Cache → service → mobile state `sf_record_type`. */
export function resolveEligibilityEmploymentType(
  cachedEmploymentType: string | null | undefined,
  serviceEmploymentType: string | undefined,
  sfRecordType: string | null | undefined
): string {
  if (cachedEmploymentType) {
    return cachedEmploymentType;
  }
  if (serviceEmploymentType) {
    return serviceEmploymentType;
  }
  return employmentTypeFromSfRecordType(sfRecordType);
}

/** Whether an eligibility field still needs user input. */
export function isEligibilityFieldValueEmpty(field: EligibilityFieldsObject): boolean {
  return field.value === null || field.value === undefined || field.value === '';
}

function _normalizeEligibilityFieldValue(field: EligibilityFieldsObject): EligibilityFieldsObject {
  return { ...field, value: field.value ?? '' };
}

/** Keeps only fields/sub-fields with empty values; normalizes empty values to `""`. */
export function filterEligibilityFieldsToCollect(fields: EligibilityFieldsObject[]): EligibilityFieldsObject[] {
  return fields
    .map((field) => {
      if (field.sub_fields?.length) {
        const emptySubFields = field.sub_fields
          .filter((subField) => isEligibilityFieldValueEmpty(subField))
          .map((subField) => _normalizeEligibilityFieldValue(subField));
        if (emptySubFields.length === 0) {
          return null;
        }
        return { ...field, sub_fields: emptySubFields };
      }

      if (!isEligibilityFieldValueEmpty(field)) {
        return null;
      }

      return _normalizeEligibilityFieldValue(field);
    })
    .filter((field): field is EligibilityFieldsObject => field !== null);
}

/** True when any top-level field or sub-field has an empty value and needs collection. */
export function eligibilityFieldsNeedCollection(fields: EligibilityFieldsObject[]): boolean {
  return filterEligibilityFieldsToCollect(fields).length > 0;
}

@Injectable({
  providedIn: 'root',
})
export class RequiredVerificationService implements OnDestroy {
  private sub = new Subscription();

  requiredVerifications: RequiredVerification[] | undefined;
  overriddenNextVerification: RequiredVerification | undefined;
  isIdentityCompleted: boolean;
  hasBeenCleared: boolean;

  company: string;
  employmentType: string;

  apiVersion = '1.4';

  electiveProducts: string[] = [];

  private companyService: CompanyService = inject(CompanyService);
  private bsCacheService: BsCacheService = inject(BsCacheService);
  private awsApiWrapper: AwsApiWrapperService = inject(AwsApiWrapperService);
  private bsHubService: BsHubService = inject(BsHubService);

  constructor(private mobile: MobileStateService, private apiSvc: ApiCacheService) {
    this.bsHubService.listen(RequiredVerificationsEnums.HUBCHANNEL, (data) => {
      if (data.payload.event === 'clear') {
        this.clearRequiredVerifications();
      }
    });
  }

  getDataToCollect(replay: boolean = false): Observable<EligibilityFieldsInterface> {
    const company = this.companyService.getCompany() || this.company;

    return forkJoin({
      cachedEmploymentType: from(this.bsCacheService.getItem('employmentType')),
      mobileState: this.mobile.get().pipe(
        take(1),
        catchError(() => of(null as MobileStateResponse | null))
      ),
    }).pipe(
      switchMap(({ cachedEmploymentType, mobileState }) => {
        const params: Record<string, string> = {};
        const employmentType = resolveEligibilityEmploymentType(
          cachedEmploymentType,
          this.employmentType,
          mobileState?.sf_record_type
        );
        this.employmentType = employmentType;

        if (company) {
          params['company'] = company;
        }
        params['employment_type'] = employmentType;

        const payload = Object.entries(params)
          .map(([key, val]) => `${key}=${val}`)
          .join('&');

        return from(
          this.awsApiWrapper.get(
            'api-mobile',
            `/client/eligibility/data-collect?fieldVersion=${this.apiVersion}&replay=${replay}&${payload}`,
            {}
          )
        ).pipe(map((response: EligibilityFieldsInterface) => this._normalizeEligibilityResponse(response)));
      })
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  clearRequiredVerifications() {
    this.hasBeenCleared = true;
    this.requiredVerifications = undefined;
    window.localStorage.removeItem(RequiredVerificationsEnums.LOCALSTORAGE);
  }

  getRequiredVerifications(replay?: boolean, skipCreation?: boolean): Observable<RequiredVerification[] | null> {
    if (replay) {
      return this.getDataToCollect(true).pipe(
        map((response) => {
          if (response.eligibility_fields && response.eligibility_fields.length > 0) {
            const newVerifications: RequiredVerification[] = [];
            response.eligibility_fields.forEach((value) => {
              newVerifications.push({ type: value.name, eligibility_field: value });
            });
            this.electiveProducts = response.elective_products ?? [];
            return newVerifications;
          } else {
            return [];
          }
        })
      );
    }

    return this.getDataToCollect(false).pipe(
      map((response) => {
        if (response.eligibility_fields && response.eligibility_fields.length > 0) {
          const newVerifications: RequiredVerification[] = [];
          this.requiredVerifications = [];

          response.eligibility_fields.forEach((value) => {
            newVerifications.push({ type: value.name, eligibility_field: value });
          });

          window.localStorage.setItem(RequiredVerificationsEnums.LOCALSTORAGE, JSON.stringify(newVerifications));
          this.requiredVerifications = newVerifications;

          this.electiveProducts = response.elective_products ?? [];

          return newVerifications;
        } else {
          this.requiredVerifications = [];
          return [];
        }
      })
    );
  }

  getVerificationOfType(type: string): RequiredVerification | undefined {
    return this.requiredVerifications?.find((verification) => verification.type === type);
  }

  setRequiredVerifications() {
    window.localStorage.setItem(RequiredVerificationsEnums.LOCALSTORAGE, JSON.stringify(this.requiredVerifications));
  }

  updateRequiredVerificationStatus(type: string, status: VerificationStatus) {
    if (this.requiredVerifications) {
      const updateVerification = this.getVerificationOfType(type);
      if (updateVerification) {
        updateVerification['status'] = status;
      } else {
        this.requiredVerifications.push({ type: type, status: status });
      }
    } else {
      this.requiredVerifications = [{ type: type, status: status }];
    }
    this.setRequiredVerifications();
  }

  getRequiredVerificationStatus(type: string): string | undefined {
    return this.getVerificationOfType(type)?.status;
  }

  getNextRequiredVerification(): Observable<RequiredVerification> {
    return this.getRequiredVerifications(false).pipe(
      map((verifications) => {
        if (verifications) {
          const nextVerification = verifications.find(
            (verification) => !verification.status || verification.status !== VerificationStatus.COMPLETED
          );
          return nextVerification ? nextVerification : { type: 'none' };
        } else {
          return { type: 'none' };
        }
      })
    );
  }

  setNextRequiredVerification(verification: RequiredVerification) {
    this.overriddenNextVerification = verification;
  }

  identityCompleted(isCompleted?: boolean) {
    this.isIdentityCompleted = true;
  }

  /** Dev-injected last 4 SSN field (align with eligibility data-collect shape). */
  private static readonly _DEV_INJECT_ELIGIBILITY_LAST4: EligibilityFieldsObject = {
    name: 'last_four_ssn',
    api_parameter_name: 'last_four_ssn',
    value: '',
  };

  private _normalizeEligibilityResponse(response: EligibilityFieldsInterface): EligibilityFieldsInterface {
    const withDevOverride = this._applyEligibilityLast4DevOverride(response);
    return {
      ...withDevOverride,
      eligibility_fields: filterEligibilityFieldsToCollect(withDevOverride.eligibility_fields ?? []),
    };
  }

  private _applyEligibilityLast4DevOverride(response: EligibilityFieldsInterface): EligibilityFieldsInterface {
    if (!readEligibilityLast4DevInject()) {
      return response;
    }
    const fields = response.eligibility_fields ?? [];
    const existingIndex = fields.findIndex((field) => field.name === 'last_four_ssn');

    if (existingIndex >= 0) {
      const updatedFields = [...fields];
      updatedFields[existingIndex] = {
        ...fields[existingIndex],
        api_parameter_name: fields[existingIndex].api_parameter_name ?? 'last_four_ssn',
        value: '',
      };
      return { ...response, eligibility_fields: updatedFields };
    }

    return {
      ...response,
      eligibility_fields: [...fields, { ...RequiredVerificationService._DEV_INJECT_ELIGIBILITY_LAST4 }],
    };
  }
}
